import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from 'react-router-dom';

import { SeverityLevel } from "@microsoft/applicationinsights-web";

import Button from '../../formElements/Button';
import {getCurrentEnvironment} from "../../navigation/General";

import classes from "../error/errorStyles/errorMessage.module.css";

const ErrorMessage = ({ appInsights }) => {
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector((state) => state.entities.participantService.currentParticipant);
    const error = useSelector((state) => state.entities.formsState.errorMessage);
    const environmentIndex = useSelector((state) => state.entities.userService.loggedInUser.environment);
    const location = useLocation();
    const navigate = useNavigate();

    const [timestamp, setTimestamp] = useState();
    const [errorMessage, setErrorMessage] = useState('');

    const onRetry = () => {
        window.location.reload();
    };

    const onNavigateHome = () => {
        navigate('/');
        window.location.reload();
    };

    const onCopyToClipboard = () => {
        const errorDetails = `
            Timestamp: ${timestamp}
            Version: ${import.meta.env.VITE_APP_VERSION + (getCurrentEnvironment(environmentIndex).sprint ? 'r' + getCurrentEnvironment(environmentIndex).sprint : '')}
            Environment: ${getCurrentEnvironment(environmentIndex).text}
            ${errorMessage ? `Error message: ${errorMessage}` : ''}
            Page: ${location.pathname}
            Azure Trace ID (operation_Id): ${appInsights.context.telemetryTrace.traceID}
            Azure User ID (user_Id): ${appInsights.context.user.id}
        `;
        navigator.clipboard.writeText(errorDetails.trim())
            .then(() => {
                alert('Error details copied to clipboard!');
            })
            .catch(err => {
                console.error('Failed to copy text: ', err);
            });
    };

    useEffect(() => {
        setTimestamp(new Date().toLocaleString());
        setErrorMessage(error);

        //additional useful trace information (add as required)
        appInsights.trackTrace({
            message: `Debug trace information for : ${errorMessage}`,
            severityLevel: SeverityLevel.Information,
            properties: {
                version: `${import.meta.env.VITE_APP_VERSION}${getCurrentEnvironment(environmentIndex).sprint ? `r${getCurrentEnvironment(environmentIndex).sprint}` : ''}`,
                screen: location.pathname,
                userRoles: roles,
                participantId: currentParticipant.id,
                ptCode: currentParticipant.ptCode
            }
        });
    }, [appInsights]);

    return (
        <div className={classes.errorMessageHeader}>
            <div className={classes.errorMessageText}>
                <h1>Oops! Something went wrong. We&#39;ve logged the error and our team is looking into it.</h1>
                <h3>Please try refreshing the page or return to the home screen. If the issue persists, please contact our support team for further assistance. Thank you for your patience.</h3>
            </div>
            <div className={classes.errorMessageText}>
                <div className={classes.errorMessageButton}>
                    <Button
                        id="retryButton"
                        onClick={onRetry}
                        content={"Retry"}
                    />
                    <h3>You can try to reload the page again.</h3>
                </div>
                <div className={classes.errorMessageButton}>
                    <Button
                        id="homeButton"
                        onClick={onNavigateHome}
                        content={"Home"}
                    />
                    <h3>If you are still having problems you can return to the home page.</h3>
                </div>
            </div>
            <div className={classes.errorMessageText}>
                <h2>Error Details:</h2>
                <p><strong>Timestamp:</strong> {timestamp}</p>
                <p>
                    <strong>Version:</strong> {import.meta.env.VITE_APP_VERSION + 'r' + getCurrentEnvironment(environmentIndex).sprint}
                </p>
                <p><strong>Environment:</strong> {getCurrentEnvironment(environmentIndex).text}</p>
                {errorMessage && <p><strong>Error message:</strong> {errorMessage}</p>}
                <p><strong>Page:</strong> {location.pathname}</p>
                <Button
                    id="copyToClipboardButton"
                    onClick={onCopyToClipboard}
                    content={"Copy to Clipboard"}
                />
            </div>
        </div>
    );
};

ErrorMessage.propTypes = {
    appInsights: PropTypes.object.isRequired
};

export default ErrorMessage;